import z from "zod";

import {
  ApplicantProfileSchema,
  ApplicantProfileUpdateSchema,
} from "@/schemas/applicantProfile";
import {
  CertificateCreateSchema,
  CertificateSchema,
} from "@/schemas/certificate";
import { CredentialCreateSchema, CredentialSchema } from "@/schemas/credential";
import { EducationCreateSchema, EducationSchema } from "@/schemas/education";
import { ExamCreateSchema, ExamSchema } from "@/schemas/exam";
import { ExperienceCreateSchema, ExperienceSchema } from "@/schemas/experience";
import { FileSchema } from "@/schemas/file";
import { LanguageCreateSchema, LanguageSchema } from "@/schemas/language";
import {
  AnswerCreateSchema,
  QuestionAndAnswerSchema,
} from "@/schemas/question";
import { ReferenceCreateSchema, ReferenceSchema } from "@/schemas/reference";
import { SchoolPreferencesSchema } from "@/schemas/schoolPreferences";
import { candidateFileIdSchema } from "@/types/candidateFile";
import { nimbleAtsJobIdSchema } from "@/types/nimbleAtsJobId";

export const ApplicationSource = {
  NIMBLE_HIRE: "nimble-hire",
  COHIRE: "cohire",
} as const;
export type ApplicationSource =
  (typeof ApplicationSource)[keyof typeof ApplicationSource];
export const ApplicationSourceSchema = z.enum(ApplicationSource);

export const ApplicationStatus = {
  DRAFT: "draft",
  DRAFT_NOT_SUBMITTABLE: "draft_not_submittable",
  SUBMITTED: "submitted",
} as const;
export type ApplicationStatus =
  (typeof ApplicationStatus)[keyof typeof ApplicationStatus];
export const ApplicationStatusSchema = z.enum(ApplicationStatus);

export const ApplicantBackgroundSchema = z.object({
  resume: FileSchema.nullable(),
  certificates: z.array(CertificateSchema).nullable(),
  education: z.array(EducationSchema).nullable(),
  experience: z.array(ExperienceSchema).nullable(),
  credentials: z.array(CredentialSchema).nullable(),
  languages: z.array(LanguageSchema).nullable(),
  references: z.array(ReferenceSchema).nullable(),
  exams: z.array(ExamSchema).nullable(),
});
export type ApplicantBackground = z.infer<typeof ApplicantBackgroundSchema>;

export const ApplicantBackgroundCreateSchema = z.object({
  resume: candidateFileIdSchema.nullish(),
  certificates: z.array(CertificateCreateSchema).nullish(),
  education: z.array(EducationCreateSchema).nullish(),
  experience: z.array(ExperienceCreateSchema).nullish(),
  credentials: z.array(CredentialCreateSchema).nullish(),
  languages: z.array(LanguageCreateSchema).nullish(),
  references: z.array(ReferenceCreateSchema).nullish(),
  exams: z.array(ExamCreateSchema).nullish(),
});
export type ApplicantBackgroundUpdate = z.infer<
  typeof ApplicantBackgroundCreateSchema
>;

export const ApplicationBaseSchema = z.object({
  jobId: nimbleAtsJobIdSchema,
});
export type ApplicationBase = z.infer<typeof ApplicationBaseSchema>;

export const ApplicationMetaSchema = z.object({
  id: z.string(),
  jobTitle: z.string(),
  districtName: z.string(),
  updated: z.string(),
  source: ApplicationSourceSchema,
  status: ApplicationStatusSchema,
});
export type ApplicationMeta = z.infer<typeof ApplicationMetaSchema>;

export const ApplicationContentSchema = z.object({
  applicantProfile: ApplicantProfileSchema.nullable(),
  applicantBackground: ApplicantBackgroundSchema,
  customQuestionsAndAnswers: z.array(QuestionAndAnswerSchema).nullable(),
  schoolPreferences: SchoolPreferencesSchema.nullable(),
});
export type ApplicationContent = z.infer<typeof ApplicationContentSchema>;

export const ApplicationUpdateSchema = z.object({
  applicantProfile: ApplicantProfileUpdateSchema.nullish(),
  applicantBackground: ApplicantBackgroundCreateSchema.nullish(),
  customQuestionsAndAnswers: z.array(AnswerCreateSchema).nullish(),
  schoolPreferences: SchoolPreferencesSchema.nullish(),
});
export type ApplicationUpdate = z.infer<typeof ApplicationUpdateSchema>;

export const ApplicationItemSchema = ApplicationBaseSchema.extend(
  ApplicationMetaSchema.shape,
);
export type ApplicationItem = z.infer<typeof ApplicationItemSchema>;

export const ApplicationSchema = ApplicationBaseSchema.extend({
  ...ApplicationMetaSchema.shape,
  ...ApplicationContentSchema.shape,
  dateSubmitted: z.string().nullable(),
});
export type Application = z.infer<typeof ApplicationSchema>;
